<template>
  <div
    class="min-h-screen flex flex-col items-center justify-center py-6 px-4 sm:px-6 lg:px-8 bg-lightGray bg-cover bg-center relative overflow-hidden"
  >
    <HyperspeedBackground class="absolute inset-0 z-0" :beam-count="120" color-scheme="rainbow" :speed-factor="1.5" />
    <div
      class="max-w-4xl w-full space-y-6 bg-white/80 backdrop-blur-xl p-8 rounded-2xl overflow-hidden shadow-2xl shadow-cosmicPurple/10 relative z-10"
    >
      <ShineBorder
        :border-width="2"
        :duration="24"
        :shine-color="['#A07CFE', '#FE8FB5', '#FFBE7B']"
      />
      <div class="relative flex items-center justify-center">
        <LogoMainLink class="z-20" />
      </div>

      <!-- Loading state -->
      <div v-if="isProcessing" class="text-center space-y-4">
        <Loader2 class="h-8 w-8 animate-spin text-gray-400 mx-auto" />
        <p class="text-gray-500">{{ loadingMessage }}</p>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="text-center space-y-4">
        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          <AlertCircle class="h-6 w-6 text-red-600" />
        </div>
        <h1 class="text-2xl text-gray-900 font-light">Something went wrong</h1>
        <p class="text-gray-600">{{ error }}</p>
      </div>

      <!-- Provide link: submitted success -->
      <div v-else-if="isSubmitted" class="text-center space-y-4">
        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
          <CheckCircle class="h-6 w-6 text-green-600" />
        </div>
        <h1 class="text-2xl text-gray-900 font-light">Thank you!</h1>
        <p class="text-gray-600">Referral link submitted successfully.</p>
      </div>

      <!-- Provide link form -->
      <div v-else-if="applications.length" class="space-y-6">
        <div class="text-center space-y-4">
          <h1 class="text-2xl text-gray-900 font-light text-center">Submit Referral Link</h1>
          <p class="text-gray-600">
            You're providing a job application link with a referral for
            <strong>{{ applications[0].applicant_name }}</strong>.
          </p>
        </div>

        <!-- Application picker (when multiple positions) -->
        <div v-if="applications.length > 1" class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">Select position</label>
          <div class="grid gap-2">
            <button
              v-for="app in applications"
              :key="app.id"
              type="button"
              class="text-left px-4 py-3 rounded-lg border transition-all duration-150"
              :class="selectedApplicationId === app.id
                ? 'border-lime-400 bg-lime-50 ring-1 ring-lime-400'
                : 'border-gray-200 hover:border-gray-300 bg-white'"
              @click="selectedApplicationId = app.id"
            >
              <span class="font-medium text-gray-900">{{ app.role_name }}</span>
              <span class="text-gray-500"> at </span>
              <span class="font-medium text-gray-900">{{ app.employer_name }}</span>
            </button>
          </div>
        </div>

        <!-- Single position display -->
        <div v-else class="text-center">
          <strong>{{ applications[0].role_name }}</strong> position at
          <strong>{{ applications[0].employer_name }}</strong>
        </div>

        <form @submit.prevent="submitLink" class="space-y-4 mx-auto">
          <div>
            <label for="referral-link" class="block text-sm font-medium text-gray-700 mb-1">
              Referral Link
            </label>
            <Input
              id="referral-link"
              v-model="referralLink"
              type="url"
              placeholder="https://..."
              required
            />
            <p v-if="submitError" class="mt-1 text-sm text-red-600">{{ submitError }}</p>
          </div>

          <button
            type="submit"
            :disabled="isSubmitting || !selectedApplicationId"
            class="w-full flex items-center justify-center gap-2 bg-lime-400 text-black/80 rounded-md py-2.5 px-4 text-sm hover:bg-lime-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Loader2 v-if="isSubmitting" class="h-4 w-4 animate-spin" />
            Submit Referral Link
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { Loader2, AlertCircle, CheckCircle } from 'lucide-vue-next'
import { API_ROUTES } from '@/const/apiRoutes'
import HyperspeedBackground from '@/components/layout/HyperspeedBackground.vue'
import ShineBorder from '@/components/ui/ShineBorder.vue'
import LogoMainLink from '@/components/common/LogoMainLink.vue'
import { Input } from '@/components/ui/input'

interface ApplicationInfo {
  id: number
  applicant_name: string
  role_name: string
  employer_name: string
}

const route = useRoute()
const router = useRouter()
const action = computed(() => route.query.action as string)

const isProcessing = ref(true)
const error = ref('')
const applications = ref<ApplicationInfo[]>([])
const selectedApplicationId = ref<number | null>(null)
const referralLink = ref('')
const isSubmitting = ref(false)
const isSubmitted = ref(false)
const submitError = ref('')

const FRIENDLY_ERROR = 'This referral request has already been updated or expired.'

function getFriendlyError(err: any, fallback: string): string {
  const msg = err.response?.data?.error
  if (err.response?.status === 400 && msg) return FRIENDLY_ERROR
  return fallback
}

const loadingMessage = computed(() => {
  if (action.value === 'confirm') return 'Confirming your referral...'
  if (action.value === 'decline') return 'Processing...'
  return 'Validating your link...'
})

onMounted(async () => {
  const token = route.params.token as string

  if (action.value === 'confirm' || action.value === 'decline') {
    // For confirm/decline, call the action endpoint and redirect to result view
    try {
      const endpoint = action.value === 'confirm'
        ? API_ROUTES.REFERRAL_ACTION_CONFIRM(token)
        : API_ROUTES.REFERRAL_ACTION_DECLINE(token)
      const { data } = await axios.post(endpoint)
      const status = action.value === 'confirm' ? 'confirmed' : 'declined'
      if (Array.isArray(data?.applications)) {
        sessionStorage.setItem(
          'referralActionResult',
          JSON.stringify({ status, applications: data.applications })
        )
      }
      await router.push({ path: '/referral-action/result', query: { status } })
      return
    } catch (err: any) {
      error.value = getFriendlyError(err, 'This link is invalid or has already been used.')
    } finally {
      isProcessing.value = false
    }
    return
  }

  // For provide-link, validate first then show form
  try {
    const { data } = await axios.get(API_ROUTES.REFERRAL_ACTION_VALIDATE(token))
    if (Array.isArray(data.applications)) {
      applications.value = data.applications
    } else if (data.application) {
      applications.value = [data.application]
    }
    if (applications.value.length === 1) {
      selectedApplicationId.value = applications.value[0].id
    }
  } catch (err: any) {
    error.value = getFriendlyError(err, 'This link is invalid.')
  } finally {
    isProcessing.value = false
  }
})

async function submitLink() {
  const token = route.params.token as string
  submitError.value = ''

  if (!referralLink.value.trim()) {
    submitError.value = 'Please enter a referral link.'
    return
  }

  if (!selectedApplicationId.value) {
    submitError.value = 'Please select a position.'
    return
  }

  isSubmitting.value = true
  try {
    await axios.post(API_ROUTES.REFERRAL_ACTION_PROVIDE_LINK(token), {
      referral_link: referralLink.value,
      application_id: selectedApplicationId.value
    })
    isSubmitted.value = true
  } catch (err: any) {
    submitError.value = getFriendlyError(err, 'Failed to submit referral link.')
  } finally {
    isSubmitting.value = false
  }
}
</script>
